import { Label, Panel, PanelType, IconButton, Spinner, SpinnerSize, DefaultButton, TextField, PrimaryButton, MessageBar, MessageBarType } from "@fluentui/react";
import Alert from '@mui/material/Alert';
import { IContent, ISharePointContent, ISiteMap } from "./DataTable";
import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { getSiteMap, getWASContent, getSharePointContent, addWASContent, updateWASContent } from "../api";
import "../styles/ContentPanel.css";

export interface ISharePointContentWithPriority extends ISharePointContent {
    coPriority?: number;
    coActive?: boolean;
    smid?: number;
}

export default function ProducttypePanel(props: { onDismiss: () => void; }): JSX.Element {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { instance, accounts } = useMsal();
    const [token, setToken] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [isDialogLoading, setIsDialogLoading] = useState<boolean>(false);
    const [sortOrder, setSortOrder] = useState<number>(0);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [wasContents, setWASContents] = useState<IContent[]>([]);
    const [wasUrlInput, setWASUrlInput] = useState<string>("");
    const [siteMap, setSiteMap] = useState<ISiteMap | null>(null);
    const [sharePointContents, setSharePointContents] = useState<ISharePointContentWithPriority[]>([]);
    const [umbracoId, setUmbracoId] = useState<string>("");
    const [wasName, setWASName] = useState<string>("");

    useEffect(() => {
        setIsLoading(true);
        instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        }).then((response) => {
            setToken(response.accessToken);
            setIsLoading(false);
        });
    }, [props]);

    const fetchSiteMap = async (accessToken: string, url: string) => {
        setIsLoading(true);
        try {
            const [siteMapResponse] = await Promise.all([
                getSiteMap(accessToken, url),
            ]);
            if (siteMapResponse && siteMapResponse?.data) {
                setSiteMap(siteMapResponse.data);
                await fetchContent(siteMapResponse.data);
            } else {
                setError("Siden ikke fundet. Prøv igen med en gyldig URL.");
            }
        } catch (error) {
            console.error(error);
            setError("Fejl ved hentning af side.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchContent = async (siteMap: ISiteMap) => {
        setIsLoading(true);
        try {
            const response = await getWASContent(token, siteMap[0].smid);
            if (response && response.data) {
                setWASContents(response.data);
                try {
                    const spResponses = await Promise.all(response.data.map(async (item: any) => {
                        try {
                            const spResponse = await getSharePointContent(token, item.scid);
                            if (!spResponse || !spResponse.data) {
                                console.warn(`No data returned for item:`, item);
                            }
                            return spResponse;
                        } catch (error) {
                            console.error(`Error fetching SharePoint content for item:`, item, error);
                            return null;
                        }
                    }));

                    const spResponse = spResponses
                        .filter(res => res && res.data)
                        .flatMap(res => res?.data || []);

                    const priorityMap = Object.fromEntries(
                        response.data.map((item: any) => [item.scid, item.coPriority])
                    );
                    const activeMap = Object.fromEntries(
                        response.data.map((item: any) => [item.scid, item.coActive])
                    );

                    const mergedSharePointContents = spResponse.map((sp: any) => ({
                        ...sp,
                        coPriority: priorityMap[sp.scid] ?? sp.coPriority,
                        coActive: activeMap[sp.scid] ?? false,
                        smid: response.data.find((item: any) => item.scid === sp.scid)?.smid || 0,
                    }));
                    setSharePointContents(mergedSharePointContents);
                } catch (spError) {
                    console.error("Fejl ved hentning af SharePoint indhold:", spError);
                    setError("Fejl ved hentning af indhold.");
                }
            } else {
                setError("Ingen WAS indhold fundet for denne side.");
            }
        } catch (error) {
            console.error("Fejl ved hentning af WAS indhold:", error);
            setError("Fejl ved hentning af WAS indhold.");
        } finally {
            setIsLoading(false);
        }
    };

    const onSortOrderChange = (sharePointID: number, newSortOrder: number) => {
        setSharePointContents(prevSharePointContents =>
            prevSharePointContents.map(sp =>
                sp.scid === sharePointID ? { ...sp, coPriority: newSortOrder } : sp
            )
        );
    };

    const onSaveWASContent = async (token: string, wasUrl: string, umbracoId: string, sortOrder: number, wasName: string) => {
        setWarningMessage(null);
        setSuccessMessage(null);
        await confirmSaveWASContent(token, wasUrl, umbracoId, sortOrder, wasName);
    };

    const confirmSaveWASContent = async (token: string, wasUrl: string, umbracoId: string, sortOrder: number, wasName: string) => {
        setIsDialogLoading(true);

        try {
            const newWASContent = await addWASContent(token, wasUrl, umbracoId, sortOrder, wasName);
            console.log("New WAS content added:", newWASContent);
            if (newWASContent && newWASContent.data) {
                setSuccessMessage("WAS indhold er blevet oprettet!");
                setWASContents([...wasContents, newWASContent]);
            }
            else {
                setWarningMessage("Kunne ikke oprette WAS indhold. Prøv igen.");
            }
        } catch (error) {
            console.error(error);
            setWarningMessage("Kunne ikke oprette WAS indhold. Prøv igen.");
        } finally {
            setIsDialogLoading(false);
        }
    };

    const handleUpdateContent = async (item: ISharePointContentWithPriority) => {
        try {
            await updateWASContent(
                token,
                item.smid,
                item.scid,
                item.coPriority || 0,
                !item.coActive
            );
            setSharePointContents(prev =>
                prev.map(sp =>
                    sp.scid === item.scid
                        ? { ...sp, coActive: !item.coActive }
                        : sp
                )
            );
            setSuccessMessage("WAS indhold opdateret!");
        } catch (err) {
            console.error("Update failed", err);
        }
    };

    const handleUpdatePriority = async (item: ISharePointContentWithPriority) => {
        try {
            await updateWASContent(
                token,
                item.smid,
                item.scid,
                item.coPriority || 0,
                item.coActive || false
            );
            setSuccessMessage("Sorteringsrækkefølge opdateret!");
        } catch (err) {
            console.error("Priority update failed", err);
            setWarningMessage("Kunne ikke opdatere rækkefølge. Prøv igen.");
        }
    };

    return (
        <Panel
            type={PanelType.largeFixed}
            isOpen={props !== null}
            onDismiss={() => props.onDismiss()}
            closeButtonAriaLabel="Close"
            className="ContentPanel"
            styles={{
                content: {
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0,
                },
                scrollableContent: {
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflowY: 'auto',
                    height: '100%'
                },
            }}
        >
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: 'white',
                    paddingBottom: '1rem ',
                    marginTop: '-18px',
                    paddingTop: '1rem',
                    width: '90%',
                }}
            >
                <h4 style={{ margin: 0, marginBottom: "0.5rem" }}>Administrer WAS indhold</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 100 }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'end' }}>
                    <TextField
                        label="Find WAS indhold på side:"
                        styles={{ root: { width: '100%' } }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWASUrlInput(e.target.value)}
                    />
                    <TextField
                        label="Umbraco ID"
                        styles={{ root: { width: '250px' } }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUmbracoId(e.target.value)}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'end' }}>
                    <TextField
                        label="Navn på Umbraco kasse"
                        styles={{ root: { width: '100%' } }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWASName(e.target.value)}
                    />
                    <TextField
                        label="Rækkefølge"
                        styles={{ root: { width: '250px' } }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSortOrder(Number(e.target.value))}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'end', marginTop: '2rem' }}>
                    <DefaultButton
                        text="Hent indhold"
                        styles={{ root: { width: '50%' } }}
                        onClick={() => fetchSiteMap(token, wasUrlInput)}
                    />
                    <PrimaryButton
                        text="Tilføj indholdsboks"
                        styles={{ root: { width: '50%' } }}
                        onClick={() => onSaveWASContent(token, wasUrlInput, umbracoId, sortOrder, wasName)}
                    />
                </div>

                {warningMessage && (
                    <MessageBar messageBarType={MessageBarType.warning} onDismiss={() => setWarningMessage(null)}>
                        {warningMessage}
                    </MessageBar>
                )}

                {successMessage && (
                    <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccessMessage(null)}>
                        {successMessage}
                    </MessageBar>
                )}
                {sharePointContents && !isLoading && (
                    <div>
                        <h6 style={{ marginTop: '2rem' }}>WAS indhold på siden:</h6>
                        {sharePointContents.map((content) => (
                            <div key={content.scid} style={{ marginBottom: '2rem' }}>
                                <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                                    <Label>{content.scName}</Label>
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end", gap: "1rem" }}>
                                        <TextField
                                            label="Sorteringsrækkefølge"
                                            type="number"
                                            value={content.coPriority?.toString() ?? ""}
                                            onChange={(_, newValue) => onSortOrderChange(content.scid, Number(newValue))}
                                            styles={{ root: { width: '150px' } }}
                                        />
                                        <IconButton
                                            iconProps={{ iconName: 'Checkmark' }}
                                            title='Opdater rækkefølge'
                                            ariaLabel='Opdater rækkefølge'
                                            onClick={() => handleUpdatePriority(content)}
                                        />
                                        <IconButton
                                            iconProps={{ iconName: !content.coActive ? 'Add' : 'Delete' }}
                                            title={content.coActive ? 'Deaktiver' : 'Aktiver'}
                                            ariaLabel={content.coActive ? 'deaktiver' : 'aktiver'}
                                            onClick={() => handleUpdateContent(content)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {isLoading && (
                    <Spinner size={SpinnerSize.large} label="..." />
                )}
                {error && (
                    <Alert severity="warning">{error}</Alert>
                )}
            </div>
        </Panel>
    );
}
